<template>
  <div class="email-view">
    <div class="container">
      <h1>Bienvenue au jeu défi iRiS de l'intuition du corps !</h1>
      <p>Pour démarrer, veuillez entrer votre adresse e-mail. Elle servira à contacter les gagnants et à vous envoyer vos résultats individuels (et uniquement à ça).<br/>
        <br/>
        Ce jeu se déroule jusqu'au 13 octobre minuit.<br/>
        Les résultats globaux et les meilleurs scores seront communiqués le 16 octobre dans la newsletter d'iRiS Intuition.
      </p>
      <form @submit.prevent="handleSubmit" class="email-form">
        <div class="form-group">
          <label for="email">E-mail:</label>
          <input
            id="email"
            v-model="email"
            type="email"
            required
            :disabled="loading"
            placeholder="Mon adresse e-mail"
          />
        </div>
        
        <button type="submit" :disabled="loading || !email">
          {{ loading ? 'Chargement...' : 'Continue' }}
        </button>
      </form>
      
      <div v-if="error" class="error">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useGameStore } from '../stores/game'

const router = useRouter()
const gameStore = useGameStore()

const email = ref('')
const loading = ref(false)
const error = ref('')

const handleSubmit = async () => {
  if (!email.value) return

  loading.value = true
  error.value = ''

  try {
    const data = await gameStore.createOrGetSession(email.value)

    // Map game routes to game names in backend
    const gameNameMap: Record<string, string> = {
      'game1': 'question',
      'game2': 'treasure',
      'game3': 'card',
      'game4': 'axis'
    }

    // Define max steps for each game
    const maxSteps: Record<string, number> = {
      'question': 4,
      'treasure': 1,
      'card': 20,
      'axis': 1
    }

    // Check if user has completed all games
    if (data.completedGames.length >= gameStore.gameNames.length) {
      router.push('/complete')
      return
    }

    // Find the first game that's not completed or is in progress
    let targetGame = 'game1'
    let foundIncomplete = false

    for (const gameRoute of gameStore.gameNames) {
      const gameName = gameNameMap[gameRoute]
      const progress = data.gameProgress?.[gameName] || 0
      const max = maxSteps[gameName] || 1

      if (progress < max) {
        // This game is not complete
        targetGame = gameRoute
        foundIncomplete = true
        break
      }
    }

    const gameIndex = gameStore.gameNames.indexOf(targetGame)
    gameStore.setCurrentGame(gameIndex)
    router.push(`/game/${targetGame}`)

  } catch (err) {
    error.value = 'Erreur technique, veuillez réessayer'
    console.error(err)
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.email-view {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gradient-primary);
  padding: var(--spacing-lg);
}

.container {
  background: var(--color-bg-card);
  padding: var(--spacing-3xl);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
  max-width: 1200px;
  width: 100%;
  backdrop-filter: blur(10px);
  border: 1px solid var(--color-grey-medium);
}

h1 {
  text-align: center;
  font-family: var(--font-title);
  font-weight: var(--weight-extrabold);
  /*color: var(--color-primary-purple);*/
  margin-bottom: var(--spacing-lg);
  font-size: 2.5rem;
}

p {
  text-align: center;
  font-family: var(--font-body);
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-2xl);
  font-size: 1.1rem;
  line-height: 1.6;
}

.email-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

label {
  font-family: var(--font-title);
  font-weight: var(--weight-semibold);
  color: var(--color-text-primary);
  font-size: 1.1rem;
}

input {
  padding: var(--spacing-lg);
  border: 2px solid var(--color-grey-medium);
  border-radius: var(--radius-md);
  font-family: var(--font-body);
  font-size: 1.1rem;
  background: var(--color-white);
  color: var(--color-text-primary);
  transition: all var(--transition-fast);
}

input:focus {
  outline: none;
  border-color: var(--color-primary-purple);
  box-shadow: 0 0 0 3px var(--color-purple-light);
}

input::placeholder {
  color: var(--color-grey);
}

button {
  padding: var(--spacing-lg) var(--spacing-xl);
  background: var(--gradient-secondary);
  color: var(--color-text-primary);
  border: none;
  border-radius: var(--radius-md);
  font-family: var(--font-title);
  font-weight: var(--weight-semibold);
  font-size: 1.1rem;
  cursor: pointer;
  transition: all var(--transition-normal);
  box-shadow: var(--shadow-md);
  min-height: 3.5rem;
}

button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
  filter: brightness(1.05);
}

button:disabled {
  background: var(--color-grey-medium);
  color: var(--color-grey-dark);
  cursor: not-allowed;
  transform: none;
  box-shadow: var(--shadow-sm);
}

.error {
  color: var(--color-primary-purple);
  text-align: center;
  margin-top: var(--spacing-lg);
  font-family: var(--font-body);
  font-weight: var(--weight-medium);
  background: var(--color-purple-light);
  padding: var(--spacing-md);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-primary-purple);
}

@media (max-width: 768px) {
  .email-view {
    padding: var(--spacing-md);
  }
  
  .container {
    padding: var(--spacing-xl);
    margin: 0;
  }
  
  h1 {
    font-size: 2rem;
  }
  
  p {
    font-size: 1rem;
  }
  
  input, button {
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .email-view {
    padding: var(--spacing-sm);
  }
  
  .container {
    padding: var(--spacing-lg);
  }
  
  h1 {
    font-size: 1.75rem;
    margin-bottom: var(--spacing-md);
  }
  
  p {
    font-size: 0.95rem;
    margin-bottom: var(--spacing-lg);
  }
  
  .email-form {
    gap: var(--spacing-lg);
  }
  
  input {
    padding: var(--spacing-md);
    font-size: 0.95rem;
  }
  
  button {
    padding: var(--spacing-md) var(--spacing-lg);
    font-size: 0.95rem;
    min-height: 3rem;
  }
}
</style>